import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import styles from "./QuoteCard.module.css";

const QuoteCard = ({
  quote,
  onKeyTariffFacts,
  formData,
  singleRateBilling,
}) => {
  const navigate = useNavigate();

  const handleAmendQuote = () => {
    sessionStorage.setItem("navigated", "true");
    navigate("/", { state: { formData } });
  };

  const renderPricingDetails = () => {
    if (quote.meterType === "Single" || singleRateBilling) {
      return <li>Unit cost: {quote.pricePerKwh1}</li>;
    }

    if (quote.meterType === "Day/Night") {
      return (
        <>
          <li>Day rate: {quote.pricePerKwh1}</li>
          <li>Night rate: {quote.pricePerKwh2}</li>
        </>
      );
    }

    if (quote.meterType === "Peak/Off-Peak") {
      return (
        <>
          <li>Peak rate: {quote.pricePerKwh1}</li>
          <li>Off-peak rate: {quote.pricePerKwh2}</li>
        </>
      );
    }

    if (quote.meterType === "RHT") {
      return (
        <>
          <li>Price1: {quote.pricePerKwh1}</li>
          <li>Price2: {quote.pricePerKwh2}</li>
        </>
      );
    }

    return <li>Unit cost: {quote.unitRate}</li>;
  };

  return (
    <article
      className={styles.quoteCard}
      aria-labelledby={`quote-title-${quote.months}`}
    >
      <div className={styles.quoteHeader}>
        <h2 id={`quote-title-${quote.months}`} className={styles.quoteTitle}>
          {quote.months} Months{" "}
          <span className={styles.quoteType}>{quote.type}</span>
        </h2>
      </div>

      <div className={styles.quoteContent}>
        <div className={styles.quoteDetails}>
          <div className={styles.fuelTypeSection}>
            <div
              className={`${styles.fuelIcon} ${quote.fuelType.toLowerCase() === "electricity" ? styles.electricity : styles.gas}`}
              aria-hidden="true"
            >
              <div className={styles.icon} aria-hidden="true"></div>
            </div>
            <div>
              <h3 className={styles.fuelType}>{quote.fuelType}</h3>
            </div>
          </div>

          <ul className={styles.costDetails}>
            {renderPricingDetails()}
            <li>Standing charge: {quote.standingCharge} per day</li>
          </ul>

          <div className={styles.features}>
            <ul>
              {quote.features.map((feature) => (
                <li key={feature} className={styles.feature}>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={styles.quotePricing}>
          <div className={styles.pricingInfo}>
            <div>
              <p className={styles.pricingLabel}>
                Estimated total cost over length of contract (ex vat):
              </p>
              <h4 className={styles.totalCost}>£{quote.estimatedTotalCost}*</h4>
            </div>

            {quote?.months >= 13 && (
              <div className={quote.months > 12 ? "" : styles.hidden}>
                <p className={styles.pricingLabel}>
                  Estimated annual cost (ex vat):
                </p>
                <h4 className={styles.monthlyCost}>
                  £{quote.extimatedAnnualCost}*
                </h4>
              </div>
            )}

            {quote?.months > 1 && (
              <div>
                <p className={styles.pricingLabel}>
                  Estimated monthly cost (ex vat):
                </p>
                <h4 className={styles.monthlyCost}>
                  £{quote.estimatedMonthlyCost}*
                </h4>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.terms}>
        <p>{quote.terms}</p>
        <p>{quote.exclusions}</p>
      </div>

      <div className={styles.quoteActions}>
        <div className="flex justify-start items-start gap-6 flex-wrap whitespace-nowrap w-full">
          <button
            type="button"
            className={styles.linkButton}
            onClick={handleAmendQuote}
            aria-label={`Amend ${quote.months} months ${quote.type} quote`}
          >
            Amend this quote ›
          </button>

          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              onKeyTariffFacts(quote);
            }}
            aria-label={`View key tariff facts for ${quote.months} months ${quote.type} quote`}
          >
            Key tariff facts, please read ›
          </button>
        </div>
      </div>
    </article>
  );
};

QuoteCard.propTypes = {
  quote: PropTypes.shape({
    months: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    fuelType: PropTypes.string.isRequired,
    meterType: PropTypes.string,
    pricePerKwh1: PropTypes.string,
    pricePerKwh2: PropTypes.string,
    unitRate: PropTypes.string,
    standingCharge: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.string).isRequired,
    estimatedTotalCost: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    extimatedAnnualCost: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    estimatedMonthlyCost: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    terms: PropTypes.string,
    exclusions: PropTypes.string,
  }).isRequired,
  onKeyTariffFacts: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  singleRateBilling: PropTypes.bool.isRequired,
};

export default QuoteCard;
